'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import ScrollToContainer from '@src/components/Shared/ScrollToContainer';
import NextImage from '@src/components/Shared/NextImage';
import HeroPrimary from '@src/components/Shared/HeroPrimary';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderButtons from '@src/components/Shared/RenderButtons';
import JsonLd from '@src/components/Shared/JsonLd';

import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import CompanyStats from '@src/components/Sections/CompanyStats';
import ServiceOverview from '@src/components/Sections/ServiceOverview';
import CarouselCards from '@src/components/Sections/CarouselCards';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';

import { SportsPageClientPropTypes } from './SportsPage.types';
import ClientsLogo from '@src/components/Sections/ClientsLogo';
import Testimonials from '@src/components/Sections/Testimonials';

const SportsPage: React.FC<SportsPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/sports`, baseURL: '' }));
  setContentType('site-page');

  const sportsJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });

  return (
    <ScrollToContainer>
      <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(sportsJsonLd) }} />

      <HeroPrimary
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#000000]'
        attr='hero-component'
        className='mb-[0rem]! flex flex-col items-center justify-between overflow-visible! pt-[6rem] pb-[3rem] md:flex-row md:px-[4rem]! md:pt-[9.3rem] md:pb-[6.3rem] lg:gap-[2rem] lg:pt-[11.2rem] lg:pb-[8.2rem] 2xl:h-[75rem]! 2xl:py-[9.6rem]'
        title={page?.hero?.items?.[0]?.title}
        slideTitle
        bodyCopy={page?.hero?.items?.[0]?.bodyCopy}
        image={page?.hero?.items?.[0]?.mediaPrimary}
        titleClassName='text-[4rem] lg:text-[6rem] pb-[9.6rem] lg:pb-[10.9rem] 2xl:tracking-[-0.2432rem] leading-[4.7rem] lg:leading-[6.9rem] md:max-w-[36.6rem] md:max-w-3xl lg:max-w-[50rem] 2xl:max-w-full 2xl:tracking-[-0.152rem]'
        imgClassName='overflow-hidden absolute! sm:relative! sm:overflow-visible! flex items-end md:items-center justify-end xl:max-h-[80%] top-[0] h-full md:w-[50%] opacity-[0.2] sm:opacity-[1] [&>img]:w-auto! md:[&>img]:max-w-[135%]! md:[&>img]:right-[0%] &>img]:lg:right-[0%] md:[&>img]:absolute!'
        bodyCopyClassName='[&>p]:text-[1.6rem] md:[&>p]:text-[1.8rem] lg:[&>p]:text-[2rem] 2xl:[&>p]:text-[2rem] [&>p]:leading-[150%] md:max-w-[34.2rem] md:max-w-[43.7rem] lg:max-w-[51.7rem] 2xl:max-w-[59rem] relative md:top-[0] mt-[-5.4rem] xsm:mt-[-5.4rem] lg:mt-[-3.4rem]'
        childrenClassName='xsm:max-w-full'
      />

      <CompanyStats data={page?.trustedBy?.items?.[0]} />

      <ImageTextSecondary
        id={page?.getHolistic?.items?.[0]?.scrollTo?.slice(1)}
        className='lg:px-[0rem] xl:px-[6.4rem]'
        imgFirstRatio={0}
        imgFirstClassName='[&>img]:min-h-[26.588rem]!'
        imgSecondClassName='lg:[&>img]:ml-[-5rem] xl:[&>img]:ml-[0rem]'
        title={page?.getHolistic?.items?.[0]?.title}
        imgURL={page?.getHolistic?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.url}
        imgAlt={page?.getHolistic?.items?.[0]?.extraAssetsCollection?.items?.[0]?.media?.description}
        imgLink={page?.getHolistic?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}
        imgCaption={page?.getHolistic?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.getHolistic?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.getHolistic?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.trustedBest?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#E6E6E6]'
        className='pt-[8rem] pb-[2rem] sm:py-[9rem]'
      >
        <SectionHeader
          className='mb-[2.4rem] h-full items-center text-center! md:mb-[3.6rem]'
          title={page?.trustedBest?.items?.[0]?.title}
          titleClassName='text-[4.8rem] leading-[5rem] tracking-[-0.0418] text-body-copy font-light mb-[2rem] max-w-[34rem] lg:max-w-[90rem] 2xl:max-w-[78rem]'
          childrenClassName='relative xl:max-w-[79rem]!'
          align='center'
        >
          <RenderBodyCopy
            className='mx-auto mt-[2.4rem] text-center font-normal text-[#494949] md:text-center lg:max-w-[79rem] 2xl:mt-[3.2rem]'
            bodyCopy={page?.trustedBest?.items?.[0]?.bodyCopy?.json?.content}
          />
          <RenderButtons
            className='[&>div>a[data-pseudo]]:text-shadow-custom justify-center'
            btnClassName='w-auto!'
            items={page?.trustedBest?.items?.[0]?.buttonCollection?.items}
          />
        </SectionHeader>

        <ClientsLogo clients={page?.trustedBest?.items} />
      </Container>

      <ImageTextSecondary
        reversed
        id={page?.nextBest?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.nextBest?.items?.[0]?.title}
        imgURL={page?.nextBest?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.nextBest?.items?.[0]?.mediaPrimary?.description}
        imgLink={page?.nextBest?.items?.[0]?.extraAssetsCollection?.items?.[0]?.url}
        imgCaption={page?.nextBest?.items?.[0]?.extraAssetsCollection?.items?.[0]?.bodyCopy}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.nextBest?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.nextBest?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <CarouselCards
        id={page?.caseStudies?.items?.[0]?.scrollTo?.slice(1)}
        attr='clinicalsetting-component'
        title={page?.caseStudies?.items?.[0]?.title}
        sectionHeaderClassName='pt-[1rem]'
        topics={page?.caseStudies?.items?.[0]?.topicCollection?.items}
        sectionHeaderContainer='lg:mb-[4.4rem]! '
      />

      <ImageTextSecondary
        id={page?.sportSpecific?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.sportSpecific?.items?.[0]?.title}
        imgURL={page?.sportSpecific?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.sportSpecific?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons items={page?.sportSpecific?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <NextImage
        id={page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.weExist?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[5.6rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExist?.items?.[0]?.title}
            titleClassName='text-left text-[2.2rem] w-full leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] sm:text-center md:leading-[3rem] md:text-[2.8rem] lg:max-w-[55rem] 2xl:max-w-[78rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            childrenClassName='relative w-full'
            align='center'
          >
            <RenderBodyCopy
              className='text-body-copy--light mx-auto mt-[2.4rem] max-w-[51.7rem] text-left sm:text-center 2xl:mt-[3.2rem] 2xl:max-w-[78.2rem]'
              bodyCopy={page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons className='justify-center' items={page?.weExist?.items?.[0]?.buttonCollection?.items} />
          </SectionHeader>
        </Container>
      </NextImage>

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <ServiceOverview
        id={page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
        description={page?.clientSuccess?.items?.[0]?.bodyCopy}
        services={page?.clientSuccess?.items?.[0]?.topicCollection?.items}
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        className='max-w-[59.8rem]'
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </ScrollToContainer>
  );
};

export default SportsPage;
